import { Component, EventEmitter, Injector, Input, OnInit, Output, SimpleChanges } from '@angular/core';
import {
  TaskServiceProxy,
  TaskWatcherServiceProxy,
  UserServiceProxy,
  CheckLists,
  AgendaType
} from '@shared/service-proxies/service-proxies';
import { AppComponentBase } from '@shared/common/app-component-base';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { UserSignalrService } from '@app/shared/layout/change-status/user-signalr.service';

@Component({
  selector: 'vf-task-contributor',
  templateUrl: './task-contributor.component.html',
})
export class TaskContributorComponent extends AppComponentBase implements OnInit {
  @Input() isForNewTask: boolean = false;
  @Input() taskId: number;
  @Input() contributors: any[] = [];
  @Input() watchers: any[] = [];
  @Input() assignedUserId?: number;
  @Input() assignedUserName?: string;
  @Input() teamMembers?: any[] = [];
  @Input() taskItem: any;
  @Input() canManageContributors: boolean = true;

  @Output() contributorsChanged = new EventEmitter<any[]>();
  @Output() contributorsSelected = new EventEmitter<any[]>();
  @Output() watchersChanged = new EventEmitter<any[]>();
  @Output() requestChecklistRefresh = new EventEmitter<void>();

  searchText: string = '';
  private searchTextChanged: Subject<string> = new Subject<string>();

  allUsers: any[] = [];
  listOfUsers: any[] = [];
  isLoading: boolean = false;
  currentUser: any = null;
  signalrEvents: Subscription = null;
  // IDs currently being added / removed — used to prevent double-clicks while
  // the server roundtrip is in flight.
  pendingUserIds: Set<number> = new Set<number>();

  // In-app confirm modal state (replaces the native window.confirm) shown when
  // removing a contributor who has edited step(s).
  showRemoveConfirm: boolean = false;
  pendingRemoveUserId: number = null;
  pendingRemoveUserName: string = '';

  constructor(
    injector: Injector,
    private _taskService: TaskServiceProxy,
    private _taskWatcherService: TaskWatcherServiceProxy,
    private _userService: UserServiceProxy,
    private _userSignalrService: UserSignalrService
  ) {
    super(injector);
  }

  ngOnInit(): void {
    this.getTenantUsers();

    this.searchTextChanged
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe(() => this.initializeListOfUsers());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['assignedUserId'] || changes['contributors'] || changes['watchers']) {
      this.initializeListOfUsers();
    }
  }

  private getTenantUsers(): void {
    this._userService.getTenantUsers().subscribe((result: any) => {
      let users: any[] = result.responseData || [];

      if (this.isForNewTask && this.taskItem?.isPrivate) {
        const teamIds = this.teamMembers.map(m => m.userId);
        users = users.filter(u => teamIds.includes(u.userId));
      } else if (!this.isForNewTask && this.taskItem?.isPrivate) {
        const teamIds = this.teamMembers.map(m => m.id);
        users = users.filter(u => teamIds.includes(u.userId));
      }

      this.allUsers = users;
      this.currentUser = this.allUsers.find(u => u.userId === this.appSession.userId);
      this.initializeListOfUsers();
    });
  }

  addContributor(user: any): void {
    let exists: boolean = false;
    let watcherIndex: number = -1;

    if (!user || !this.canManageContributors) {
      return;
    }
    // Guard against double-clicks while a previous add for this user is still
    // in flight.
    if (this.pendingUserIds.has(user.id)) {
      return;
    }
    exists = this.contributors.some(c => (c.assignedToId || c.userId || c.id) === user.id);
    if (exists) {
      return;
    }

    // Role exclusivity: silently demote watcher if user is currently a watcher
    watcherIndex = this.watchers?.findIndex(w => (w.userId || w.id) === user.id) ?? -1;
    if (watcherIndex > -1 && !this.isForNewTask) {
      this._taskWatcherService.unwatchTask(user.id, this.taskId).subscribe(() => {
        this.watchers.splice(watcherIndex, 1);
        this.watchersChanged.emit(this.watchers);
      });
    } else if (watcherIndex > -1 && this.isForNewTask) {
      this.watchers.splice(watcherIndex, 1);
      this.watchersChanged.emit(this.watchers);
    }

    if (this.isForNewTask) {
      this.contributors.push({
        assignedToId: user.id,
        name: user.name,
        profilePictureUrl: user.profilePictureUrl,
        userInitials: this.getInitials(user.name)
      });
      this.contributorsSelected.emit(this.contributors);
      this.initializeListOfUsers();
      return;
    }

    // Optimistic update — apply UI changes immediately so the picker feels
    // instant, then fire the API calls in the background.
    const optimisticEntry = {
      assignedToId: user.id,
      name: user.name,
      profilePictureUrl: user.profilePictureUrl,
      userInitials: this.getInitials(user.name)
    };
    this.contributors.push(optimisticEntry);
    this.listOfUsers = this.listOfUsers.filter(u => u.id !== user.id);
    this.contributorsChanged.emit(this.contributors);
    this.pendingUserIds.add(user.id);

    this.createAutoStepForUser(user, () => {
      this.ensureAssigneeAutoStep(() => {
        this.requestChecklistRefresh.emit();
        this.pendingUserIds.delete(user.id);
      });
    });
  }

  removeContributor(userId: number, suppressConfirm: boolean = false): void {
    let stepsForUser: any[] = [];
    let hasEditedSteps: boolean = false;
    let removed: any = null;

    if (!userId || !this.canManageContributors) {
      return;
    }
    if (this.pendingUserIds.has(userId)) {
      return;
    }
    if (this.isForNewTask) {
      this.contributors = this.contributors.filter(c => (c.assignedToId || c.userId || c.id) !== userId);
      this.contributorsSelected.emit(this.contributors);
      this.initializeListOfUsers();
      return;
    }

    stepsForUser = (this.taskItem?.taskCheckLists || []).filter(cl => cl.assignedToId === userId);
    hasEditedSteps = stepsForUser.some(s => !this.isPlaceholderStep(s, userId));

    if (hasEditedSteps && !suppressConfirm) {
      // Show the in-app confirm modal instead of the native window.confirm.
      removed = this.contributors.find(c => (c.assignedToId || c.userId || c.id) === userId);
      this.pendingRemoveUserId = userId;
      this.pendingRemoveUserName = removed ? (removed.name || removed.userName || '') : '';
      this.showRemoveConfirm = true;
      return;
    }

    this.proceedRemoveContributor(userId);
  }

  confirmRemoveContributor(): void {
    const userId: number = this.pendingRemoveUserId;

    this.showRemoveConfirm = false;
    this.pendingRemoveUserId = null;
    this.pendingRemoveUserName = '';
    if (userId) {
      this.proceedRemoveContributor(userId);
    }
  }

  cancelRemoveContributor(): void {
    this.showRemoveConfirm = false;
    this.pendingRemoveUserId = null;
    this.pendingRemoveUserName = '';
  }

  private proceedRemoveContributor(userId: number): void {
    let stepsForUser: any[] = [];
    const removed = this.contributors.find(c => (c.assignedToId || c.userId || c.id) === userId);

    if (!userId) {
      return;
    }
    stepsForUser = (this.taskItem?.taskCheckLists || []).filter(cl => cl.assignedToId === userId);

    // Optimistic update — drop from contributors and put back into selectable
    // list immediately, then fire the API calls in the background.
    this.contributors = this.contributors.filter(c => (c.assignedToId || c.userId || c.id) !== userId);
    this.contributorsChanged.emit(this.contributors);
    if (removed) {
      const fullName: string = removed.name || removed.userName || '';
      this.listOfUsers = [
        ...this.listOfUsers,
        {
          id: userId,
          name: fullName,
          email: removed.email,
          profilePictureUrl: removed.profilePictureUrl ?? null,
          isContributing: false,
          userInitials: this.getInitials(fullName)
        }
      ];
    }

    // Optimistically reflect the step changes in the local list so the Steps
    // tab updates immediately: drop placeholder auto-steps, unassign edited
    // steps. The backend reload (requestChecklistRefresh) then confirms.
    if (this.taskItem && Array.isArray(this.taskItem.taskCheckLists)) {
      this.taskItem.taskCheckLists = this.taskItem.taskCheckLists
        .filter((cl: any) => !(cl.assignedToId === userId && this.isPlaceholderStep(cl, userId)));
      this.taskItem.taskCheckLists.forEach((cl: any) => {
        if (cl.assignedToId === userId) {
          cl.assignedToId = undefined;
        }
      });
    }

    this.pendingUserIds.add(userId);

    this.processRemoveSteps(stepsForUser, userId, () => {
      this.requestChecklistRefresh.emit();
      if (this.contributors.length === 0) {
        this.cleanupAssigneeAutoStepIfPlaceholder();
      }
      this.pendingUserIds.delete(userId);
    });
  }

  private processRemoveSteps(stepsForUser: any[], userId: number, onComplete: () => void): void {
    let pending: number = stepsForUser.length;

    if (pending === 0) {
      onComplete();
      return;
    }

    stepsForUser.forEach((step) => {
      if (this.isPlaceholderStep(step, userId)) {
        // Hard-delete placeholder steps
        this._taskService.deleteCheckList(
          step.id,
          this.taskItem?.keyId,
          step.keyId,
          this.taskItem?.odataEtagId,
          this.taskItem?.odataEtagDescId,
          5,
          this.taskItem?.id || this.taskId
        ).subscribe({
          next: () => {
            pending--;
            if (pending === 0) { onComplete(); }
          },
          error: () => {
            pending--;
            if (pending === 0) { onComplete(); }
          }
        });
      } else {
        // Unassign edited steps (preserve the work, mark as shared)
        const updated: CheckLists = new CheckLists();
        updated.id = step.id;
        updated.title = step.title;
        updated.description = step.description;
        updated.taskId = step.taskId;
        updated.code = step.code;
        updated.sortOrder = step.sortOrder;
        updated.agendaType = step.agendaType;
        updated.assignedToId = undefined;
        updated.actionPerformed = 'unassignContributor';
        this._taskService.createOrUpdateCheckList(updated).subscribe({
          next: () => {
            pending--;
            if (pending === 0) { onComplete(); }
          },
          error: () => {
            pending--;
            if (pending === 0) { onComplete(); }
          }
        });
      }
    });
  }

  private createAutoStepForUser(user: any, onComplete: () => void): void {
    const input: CheckLists = new CheckLists();

    input.id = 0;
    input.title = user.name;
    input.description = '';
    input.taskId = this.taskId;
    input.code = 101;
    input.assignedToId = user.id;
    input.agendaType = AgendaType._2;
    input.sortOrder = (this.taskItem?.taskCheckLists?.length || 0) + 1;
    input.actionPerformed = 'addContributorAutoStep';

    this._taskService.createOrUpdateCheckList(input).subscribe({
      next: () => this.refreshChecklists(onComplete),
      error: () => onComplete()
    });
  }

  private ensureAssigneeAutoStep(onComplete: () => void): void {
    let assigneeHasSteps: boolean = false;
    const assignee = this.allUsers.find(u => u.userId === this.assignedUserId);

    if (!this.assignedUserId || !assignee) {
      onComplete();
      return;
    }
    assigneeHasSteps = (this.taskItem?.taskCheckLists || []).some(cl => cl.assignedToId === this.assignedUserId);
    if (assigneeHasSteps) {
      onComplete();
      return;
    }

    const input: CheckLists = new CheckLists();
    input.id = 0;
    input.title = assignee.fullname || assignee.name;
    input.description = '';
    input.taskId = this.taskId;
    input.code = 101;
    input.assignedToId = this.assignedUserId;
    input.agendaType = AgendaType._2;
    input.sortOrder = (this.taskItem?.taskCheckLists?.length || 0) + 1;
    input.actionPerformed = 'addAssigneeAutoStep';

    this._taskService.createOrUpdateCheckList(input).subscribe({
      next: () => this.refreshChecklists(onComplete),
      error: () => onComplete()
    });
  }

  private refreshChecklists(onComplete: () => void): void {
    if (!this.taskId) {
      onComplete();
      return;
    }
    this._taskService.getCheckListByTaskId(this.taskId).subscribe({
      next: (result: any) => {
        if (!result.isError && result.responseData) {
          this.taskItem.taskCheckLists = result.responseData;
        }
        onComplete();
      },
      error: () => onComplete()
    });
  }

  private cleanupAssigneeAutoStepIfPlaceholder(): void {
    let assigneeSteps: any[] = [];
    const assignee = this.allUsers.find(u => u.userId === this.assignedUserId);

    if (!this.assignedUserId || !assignee) {
      return;
    }
    assigneeSteps = (this.taskItem?.taskCheckLists || []).filter(cl => cl.assignedToId === this.assignedUserId);
    if (assigneeSteps.length !== 1) {
      return;
    }
    const onlyStep = assigneeSteps[0];
    const assigneeName: string = assignee.fullname || assignee.name || '';
    const isPlaceholder: boolean = onlyStep.title === assigneeName &&
                                   (!onlyStep.description || onlyStep.description.trim() === '');
    if (!isPlaceholder) {
      return;
    }
    this._taskService.deleteCheckList(
      onlyStep.id,
      this.taskItem?.keyId,
      onlyStep.keyId,
      this.taskItem?.odataEtagId,
      this.taskItem?.odataEtagDescId,
      5,
      this.taskItem?.id || this.taskId
    ).subscribe(() => {
      this.requestChecklistRefresh.emit();
    });
  }

  private isPlaceholderStep(step: any, userId: number): boolean {
    let placeholder: boolean = false;
    const user = this.allUsers.find(u => u.userId === userId);

    if (!user || !step) {
      return placeholder;
    }
    const userName: string = user.fullname || user.name || '';
    placeholder = step.title === userName && (!step.description || step.description.trim() === '');
    return placeholder;
  }

  private initializeListOfUsers(): void {
    let contributorIds: number[] = [];

    if (!this.allUsers?.length) {
      this.listOfUsers = [];
      return;
    }
    contributorIds = (this.contributors || []).map(c => c.assignedToId || c.userId || c.id);

    // Exclude assignee AND already-selected contributors from the picker list.
    // Selected contributors appear only in the bottom "Contributing (N)" chip section.
    this.listOfUsers = this.allUsers
      .filter(user => user.userId !== this.assignedUserId && !contributorIds.includes(user.userId))
      .map(user => {
        const fullName: string = user.fullname || user.name || '';
        return {
          id: user.userId,
          name: fullName,
          email: user.email,
          profilePictureUrl: user.profilePictureUrl ?? null,
          isContributing: false,
          userInitials: this.getInitials(fullName)
        };
      });
  }

  onSearchTextChange(): void {
    this.searchTextChanged.next(this.searchText);
  }

  toggleUser(user: any): void {
    if (!user || this.pendingUserIds.has(user.id)) {
      return;
    }
    if (user.isContributing) {
      this.removeContributor(user.id);
    } else {
      this.addContributor(user);
    }
  }

  isPending(user: any): boolean {
    let result: boolean = false;
    if (!user) {
      return result;
    }
    result = this.pendingUserIds.has(user.id);
    return result;
  }

  get filteredListOfUsers(): any[] {
    const q: string = (this.searchText || '').toLowerCase();
    return this.listOfUsers.filter(u => !q || u.name?.toLowerCase().includes(q));
  }

  get currentContributorChips(): any[] {
    return this.contributors || [];
  }

  getChipName(contributor: any): string {
    let name: string = '';

    if (!contributor) {
      return name;
    }
    // Backend `TaskContributorDto` sends first and last name separately.
    // Combine them for display; the optimistic local entry already stores
    // the full name in `name`, so concatenation gracefully no-ops there.
    const first: string = contributor.name || contributor.userName || contributor.fullName || '';
    const last: string = contributor.surname || '';
    name = (first + (last ? ' ' + last : '')).trim();
    return name;
  }

  getChipInitials(contributor: any): string {
    let initials: string = '';
    const source: string = this.getChipName(contributor);

    if (!source) {
      return initials;
    }
    // Contributor chips always use a single-character initial.
    initials = source.trim().charAt(0).toUpperCase();
    return initials;
  }

  getContributorId(contributor: any): number {
    let id: number = 0;
    if (!contributor) {
      return id;
    }
    id = contributor.assignedToId || contributor.userId || contributor.id || 0;
    return id;
  }

  isAssignee(user: any): boolean {
    let result: boolean = false;
    if (!user || !this.assignedUserId) {
      return result;
    }
    result = user.id === this.assignedUserId || user.userId === this.assignedUserId;
    return result;
  }
}
